import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import type { Task } from '@core/types/task';
import { Tooltip } from '@/components/Tooltip';
import { cachedTask, loadTaskReference, referenceScope } from './cache';
import { formatDateTime, getTaskKey, taskStatusMap } from '@/features/task/logic';

export const TaskReference = observer(function TaskReference({ id, className = '', fallbackTitle, showKey = true }: { id: string; className?: string; fallbackTitle?: string; showKey?: boolean }) {
  const { t } = useI18n();
  const scope = referenceScope();
  const key = `${scope}|${id}`;
  const [resolved, setResolved] = useState<{ key: string; task: Task | null }>();
  const task = resolved?.key === key ? resolved.task : cachedTask(id, scope);
  useEffect(() => {
    let active = true;
    void loadTaskReference(id, scope).then((value) => { if (active) setResolved({ key, task: value }); });
    return () => { active = false; };
  }, [id, key, scope]);
  const title = task?.title || (task === undefined && fallbackTitle) || t(task === undefined ? 'references.loadingTask' : 'references.unavailableTask');
  const itemKey = showKey ? getTaskKey(task) : '';
  const statusKey = task?.status ? taskStatusMap[task.status]?.labelKey : undefined;
  const content = <div className="space-y-2">
    {itemKey && <p className="text-xs font-medium text-muted-foreground">{itemKey}</p>}
    <p className="font-semibold break-words">{title}</p>
    {statusKey && <p className="text-xs text-muted-foreground">{t(statusKey)}</p>}
    {task?.description && <p className="line-clamp-3 whitespace-pre-wrap break-words text-sm text-muted-foreground">{task.description}</p>}
    {task?.due_date && <p className="text-xs text-muted-foreground">{t('task.field.dueDate')}: {formatDateTime(task.due_date)}</p>}
    {task && <Link className="inline-block pt-1 text-sm text-primary hover:underline" to={`/tasks/${id}`}>{t('references.openTask')}</Link>}
  </div>;
  return <Tooltip label={title} content={content} side="bottom" className="max-w-full min-w-0 align-middle">
    <Link to={`/tasks/${id}`} onClick={(event) => event.stopPropagation()} aria-haspopup="dialog" aria-busy={task === undefined} className={`inline-flex min-w-0 max-w-full items-center gap-1 rounded-sm text-primary hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}>
      {itemKey && <span className="shrink-0 text-xs text-muted-foreground">{itemKey}</span>}
      <span className="truncate">{title}</span>
    </Link>
  </Tooltip>;
});
